/**
 * The Portfolio header stat band — org-level aggregates rendered as StatCards.
 * Pure/presentational: it takes the summary (or `undefined` while loading) and
 * renders a responsive grid, so it stories without a query.
 */
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { PortfolioSummary } from '@repo/shared/common';
import { SimpleGrid, StatCard, Skeleton, Stack, Group, Loader, Text } from '~/components/ui';
import { formatInteger } from './format';

export interface PortfolioStatsProps {
  readonly summary?: PortfolioSummary | undefined;
  readonly isLoading?: boolean;
}

/** Percentage of loans ready, rounded — or null when the portfolio is empty. */
function readyPercent(ready: number, total: number): number | null {
  return total <= 0 ? null : Math.round((ready / total) * 100);
}

export function PortfolioStats({ summary, isLoading = false }: PortfolioStatsProps): ReactNode {
  const { t } = useTranslation();

  if (isLoading || summary === undefined) {
    return (
      <SimpleGrid cols={{ base: 2, lg: 3 }} spacing="md" data-testid="portfolio-stats-loading">
        {Array.from({ length: 6 }, (_, i) => (
          <StatCard key={i} label="" value={<Skeleton height={28} width="60%" />} />
        ))}
      </SimpleGrid>
    );
  }

  const total = summary.totalLoans;
  const readyMeta = (ready: number): string | undefined => {
    const percent = readyPercent(ready, total);
    return percent === null ? undefined : t('portfolio.stats.ofLoans', { percent });
  };

  return (
    <Stack gap="sm">
      {summary.pendingRecomputeCount > 0 ? (
        // Quiet "updating…" indicator: the projection this screen reads is still
        // catching up after a large upload/delete. Polls (see usePortfolioSummary)
        // and clears itself once the dirty-loan queue drains to zero.
        <Group gap="xs" data-testid="portfolio-updating">
          <Loader size="xs" />
          <Text size="sm" c="dimmed">
            {t('portfolio.updating', { count: summary.pendingRecomputeCount })}
          </Text>
        </Group>
      ) : null}
      <SimpleGrid cols={{ base: 2, lg: 3 }} spacing="md" data-testid="portfolio-stats">
        <StatCard label={t('portfolio.stats.totalLoans')} value={formatInteger(total)} />
        <StatCard
          label={t('portfolio.stats.snapshots')}
          value={formatInteger(summary.totalSnapshots)}
        />
        <StatCard
          label={t('portfolio.stats.transactions')}
          value={formatInteger(summary.totalTransactions)}
        />
        <StatCard
          label={t('portfolio.stats.creditLossReady')}
          value={formatInteger(summary.creditLossReadyLoans)}
          meta={readyMeta(summary.creditLossReadyLoans)}
        />
        <StatCard
          label={t('portfolio.stats.payoffReady')}
          value={formatInteger(summary.payoffReadyLoans)}
          meta={readyMeta(summary.payoffReadyLoans)}
        />
        <StatCard
          label={t('portfolio.stats.prepaymentReady')}
          value={formatInteger(summary.prepaymentReadyLoans)}
          meta={readyMeta(summary.prepaymentReadyLoans)}
        />
      </SimpleGrid>
    </Stack>
  );
}
